import {AfterViewInit, inject, Injectable, signal} from '@angular/core';
import {BehaviorSubject} from "rxjs";
import {createLogger} from "../logger/logger";
import {AuthService} from "../../../core/services/auth.service";
import {UserService} from "../../../core/services/user.service";
import {Router} from "@angular/router";
import {RuntimeService} from "../../../core/services/runtime.service";
import Swal from 'sweetalert2';

const logger = createLogger('LoginModalService');

@Injectable({
    providedIn: 'root'
})
export class LoginModalService implements AfterViewInit {
    private _phoneNumberSubject = new BehaviorSubject<string>('');
    currentPhoneNumber$ = this._phoneNumberSubject.asObservable();
    private showModalSignal = signal(false); // Signal for modal visibility
    showModal = this.showModalSignal.asReadonly(); // Expose as readonly signal
    private checkoutFormData: any;
    private loginSuccessCallback?: (token: string) => Promise<void>;
    private userNotFoundCallback?: (phone: string) => void;

    readonly #runtimeService = inject(RuntimeService);

    constructor(
        private authService: AuthService,
        private userService: UserService,
        private router: Router
    ) {
    }

    async ngAfterViewInit(): Promise<void> {
        if (this.#runtimeService.isBrowser && !this.authService.recaptchaVerifier) {
            await this.authService.initRecaptchaVerifier('recaptcha-container');
        }
    }

    async show(phoneNumber: string, callback?: (token: string) => Promise<void>) {
        this.showModalSignal.set(true);
        this.loginSuccessCallback = callback;
        if (phoneNumber) {
            this._phoneNumberSubject.next(phoneNumber);
        }
        setTimeout(async () => {
            if (this.showModalSignal() && this.#runtimeService.isBrowser && !this.authService.recaptchaVerifier) {
                try {
                    await this.authService.initRecaptchaVerifier('recaptcha-container');
                } catch (error) {
                    logger.error('Failed to initialize reCAPTCHA:', error);
                    await Swal.fire('Error', 'Failed to initialize reCAPTCHA. Please try again.', 'error');
                }
            }
        }, 100);
    }

    hide() {
        this.showModalSignal.set(false);
    }

    async notifyLoginSuccess(token: string) {
        await this.userService.fetchCurrentUser(['profile']);
        if (this.loginSuccessCallback) {
            await this.loginSuccessCallback(token);
        }
        this.hide();

        if (this.#runtimeService.isBrowser) {
            const redirectUrl = localStorage.getItem('redirectUrl');
            if (redirectUrl) {
                localStorage.removeItem('redirectUrl');
                await this.router.navigateByUrl(redirectUrl);
            }
        }
    }

    notifyUserNotFound(phone: string) {
        if (this.userNotFoundCallback) this.userNotFoundCallback(phone);
    }

    setUserNotFoundCallback(callback: (phone: string) => void) {
        this.userNotFoundCallback = callback;
    }

    setCheckoutFormData(data: any) {
        this.checkoutFormData = data;
    }

    getCheckoutFormData() {
        return this.checkoutFormData;
    }
}
